/**
 * Admin / HyperMem API client (v2 port of ando-frontend's
 * ``app/api/admin/{hypermem,workflowSuggestions,nudgesAdmin}.ts``).
 *
 * Cognitive-memory governance surface. Thin typed wrappers over
 * ``apiClient`` — every endpoint is gated by ``require_platform_admin`` on
 * the BE. Route prefix on the wire is ``/api/v1/admin/hypermem/*`` (+
 * ``/admin/prompts/*`` for the evolution / approval-queue tabs, which call
 * ``apiClient`` directly from their components, mirroring v1).
 *
 * Types mirror the BE response shapes in
 * ``src/api/v1/endpoints/{hypermem_admin,workflow_suggestions,nudges}.py``.
 */

import { apiClient } from "../ApiClient.js";
import type { NudgeType, NudgeUrgency } from "../../ws/types.js";

// ── State snapshot ─────────────────────────────────────────────────────────

export interface HypermemState {
  tenant_id: string;
  episodic_facts_count: number;
  semantic_facts_count: number;
  active_episodic_facts_count: number;
  valid_semantic_facts_count: number;
  salience_scores_count: number;
  consolidation_jobs_24h: number;
  last_consolidation_run: string | null;
  pending_workflow_suggestions: number;
  active_experiments: number;
  active_prompt_versions: number;
}

export interface HypermemFactSummary {
  fact_id: string;
  user_id: string;
  fact_text: string;
  confidence: number;
  salience_score: number | null;
  created_at: string;
  is_active: boolean;
}

export interface HypermemConsolidationJob {
  id: string;
  job_type: string;
  status: string;
  duration_ms: number;
  items_processed: number;
  error: string | null;
  created_at: string;
}

export interface HypermemExperiment {
  id: string;
  name: string;
  specialist_name: string;
  hypothesis: string | null;
  status: string;
  control_version_id: string;
  treatment_version_id: string;
  control_traffic_pct: number;
  treatment_traffic_pct: number;
  control_total: number;
  control_success: number;
  treatment_total: number;
  treatment_success: number;
  min_sample_size: number;
  created_at: string;
}

export interface HypermemAuditEvent {
  id: string;
  event_type: string;
  user_id: string | null;
  intent_id: string | null;
  proposal_id: string | null;
  action_type: string | null;
  outcome: string | null;
  policy_change_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

// ── Per-tenant comparative ────────────────────────────────────────────────

export interface HypermemTenantStateRow {
  tenant_id: string;
  organization_name: string | null;
  episodic_facts_count: number;
  semantic_facts_count: number;
  semantic_facts_valid_count: number;
  salience_scores_count: number;
  pending_workflow_suggestions: number;
  pending_nudges_count: number;
  last_user_activity: string | null;
}

export interface HypermemTenantsComparativeResponse {
  tenants: HypermemTenantStateRow[];
  total_tenants: number;
  generated_at: string;
}

export type HypermemTenantSortColumn =
  | "episodic_facts_count"
  | "semantic_facts_count"
  | "semantic_facts_valid_count"
  | "salience_scores_count"
  | "pending_workflow_suggestions"
  | "pending_nudges_count"
  | "last_user_activity"
  | "organization_name";

// ── Workflow suggestions ──────────────────────────────────────────────────

export type WorkflowSuggestionStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "superseded";

export interface WorkflowSuggestionSignature {
  action_type: string;
  target_kind: string;
  temporal_cadence: string;
}

export interface WorkflowSuggestionItem {
  id: string;
  user_id: string;
  tenant_id: string;
  pattern_hash: string;
  signature: WorkflowSuggestionSignature;
  occurrences: number;
  suggested_workflow_name: string;
  last_seen: string;
  status: WorkflowSuggestionStatus;
  created_at: string;
  automation_id: string | null;
  decided_at: string | null;
}

export interface WorkflowSuggestionDetail extends WorkflowSuggestionItem {
  blueprint_template: Record<string, unknown>;
  sample_fact_ids: string[];
  sample_facts: Array<Record<string, unknown>>;
  decision_reason: string | null;
  decided_by: string | null;
}

export interface ApproveSuggestionRequest {
  edit_prompt?: string;
  edit_name?: string;
}

export interface ApproveSuggestionResponse {
  suggestion_id: string;
  automation_id: string;
  automation_name: string;
  status: string;
}

export interface RejectSuggestionResponse {
  suggestion_id: string;
  status: string;
}

// ── Nudges governance ─────────────────────────────────────────────────────

export type AdminNudgeStatus =
  | "pending"
  | "delivered"
  | "acted"
  | "dismissed"
  | "snoozed"
  | "expired";

export interface AdminNudgeRow {
  id: string;
  user_id: string;
  tenant_id: string;
  nudge_type: NudgeType;
  urgency: NudgeUrgency;
  confidence: number;
  status: AdminNudgeStatus;
  created_at: string;
  delivered_at: string | null;
  target_at: string | null;
  title: string;
  body_preview: string;
  redacted?: boolean;
}

export interface AdminNudgeListResponse {
  items: AdminNudgeRow[];
  count: number;
  tenant_id?: string;
}

export interface NudgeStatsRow {
  nudge_type: NudgeType;
  total: number;
  delivered: number;
  pending: number;
  acted: number;
  dismissed: number;
  snoozed: number;
  expired: number;
  avg_confidence: number | null;
}

export interface NudgeStatsResponse {
  tenant_id: string;
  days: number;
  by_type: NudgeStatsRow[];
}

// ── Query-string helper ───────────────────────────────────────────────────

function qs(params: Record<string, string | number | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

// ── Endpoints ─────────────────────────────────────────────────────────────

/** GET ``/admin/hypermem/state`` — platform-wide when ``tenantId`` is null. */
export async function getHypermemState(
  tenantId: string | null,
): Promise<HypermemState> {
  return apiClient.get<HypermemState>(
    `/admin/hypermem/state${tenantId ? qs({ tenant_id: tenantId }) : ""}`,
  );
}

/** GET ``/admin/hypermem/state/by-tenant`` — comparative, server-sorted. */
export async function getHypermemStateByTenant(opts?: {
  limit?: number;
  offset?: number;
  sortBy?: HypermemTenantSortColumn;
  sortDir?: "asc" | "desc";
}): Promise<HypermemTenantsComparativeResponse> {
  return apiClient.get<HypermemTenantsComparativeResponse>(
    `/admin/hypermem/state/by-tenant${qs({
      limit: opts?.limit,
      offset: opts?.offset,
      sort_by: opts?.sortBy,
      sort_dir: opts?.sortDir,
    })}`,
  );
}

/** GET ``/admin/hypermem/facts/{pillar}`` — paginated fact browser (PII). */
export async function getHypermemFacts(
  pillar: "episodic" | "semantic",
  tenantId: string,
  opts?: { user_id?: string; limit?: number; offset?: number },
): Promise<{ facts: HypermemFactSummary[]; total: number }> {
  return apiClient.get<{ facts: HypermemFactSummary[]; total: number }>(
    `/admin/hypermem/facts/${pillar}${qs({
      tenant_id: tenantId,
      user_id: opts?.user_id,
      limit: opts?.limit ?? 50,
      offset: opts?.offset ?? 0,
    })}`,
  );
}

/** GET ``/admin/hypermem/jobs/recent``. */
export async function getHypermemJobs(opts?: {
  tenant_id?: string;
  limit?: number;
}): Promise<{ jobs: HypermemConsolidationJob[] }> {
  return apiClient.get<{ jobs: HypermemConsolidationJob[] }>(
    `/admin/hypermem/jobs/recent${qs({
      tenant_id: opts?.tenant_id,
      limit: opts?.limit ?? 20,
    })}`,
  );
}

/** GET ``/admin/hypermem/experiments``. */
export async function getHypermemExperiments(
  tenantId: string,
): Promise<{ experiments: HypermemExperiment[] }> {
  return apiClient.get<{ experiments: HypermemExperiment[] }>(
    `/admin/hypermem/experiments${qs({ tenant_id: tenantId })}`,
  );
}

/** GET ``/admin/hypermem/audit``. */
export async function getHypermemAudit(
  tenantId: string,
  opts?: { event_type?: string; user_id?: string; limit?: number },
): Promise<{ events: HypermemAuditEvent[] }> {
  return apiClient.get<{ events: HypermemAuditEvent[] }>(
    `/admin/hypermem/audit${qs({
      tenant_id: tenantId,
      event_type: opts?.event_type,
      user_id: opts?.user_id,
      limit: opts?.limit ?? 100,
    })}`,
  );
}

/** GET ``/admin/hypermem/workflow-suggestions``. */
export async function listWorkflowSuggestions(
  tenantId: string,
  status: WorkflowSuggestionStatus | string = "pending",
  limit = 50,
  offset = 0,
): Promise<WorkflowSuggestionItem[]> {
  return apiClient.get<WorkflowSuggestionItem[]>(
    `/admin/hypermem/workflow-suggestions${qs({
      tenant_id: tenantId,
      status,
      limit,
      offset,
    })}`,
  );
}

/** GET ``/admin/hypermem/workflow-suggestions/{id}``. */
export async function getWorkflowSuggestionDetail(
  suggestionId: string,
): Promise<WorkflowSuggestionDetail> {
  return apiClient.get<WorkflowSuggestionDetail>(
    `/admin/hypermem/workflow-suggestions/${encodeURIComponent(suggestionId)}`,
  );
}

/** POST ``/admin/hypermem/workflow-suggestions/{id}/approve``. */
export async function approveWorkflowSuggestion(
  suggestionId: string,
  body: ApproveSuggestionRequest = {},
): Promise<ApproveSuggestionResponse> {
  return apiClient.post<ApproveSuggestionResponse>(
    `/admin/hypermem/workflow-suggestions/${encodeURIComponent(suggestionId)}/approve`,
    body,
  );
}

/** POST ``/admin/hypermem/workflow-suggestions/{id}/reject``. */
export async function rejectWorkflowSuggestion(
  suggestionId: string,
  reason: string,
): Promise<RejectSuggestionResponse> {
  return apiClient.post<RejectSuggestionResponse>(
    `/admin/hypermem/workflow-suggestions/${encodeURIComponent(suggestionId)}/reject`,
    { reason },
  );
}

/** GET ``/admin/hypermem/nudges``. */
export async function listAdminNudges(
  tenantId: string,
  opts?: {
    status?: AdminNudgeStatus;
    nudgeType?: NudgeType;
    limit?: number;
    offset?: number;
  },
): Promise<AdminNudgeListResponse> {
  return apiClient.get<AdminNudgeListResponse>(
    `/admin/hypermem/nudges${qs({
      tenant_id: tenantId,
      status: opts?.status,
      nudge_type: opts?.nudgeType,
      limit: opts?.limit ?? 50,
      offset: opts?.offset ?? 0,
    })}`,
  );
}

/** GET ``/admin/hypermem/nudges/stats``. */
export async function getAdminNudgeStats(
  tenantId: string,
  days = 7,
): Promise<NudgeStatsResponse> {
  return apiClient.get<NudgeStatsResponse>(
    `/admin/hypermem/nudges/stats${qs({ tenant_id: tenantId, days })}`,
  );
}

// ── Reasoning traces (HyperMem Layer 3) ──────────────────────────────────

export type ReasoningOutcome =
  | "answered"
  | "proposed_action"
  | "asked_clarification"
  | "refused"
  | "error";

export interface ReasoningTraceListItem {
  id: string;
  turn_id: string;
  conversation_id: string | null;
  user_id: string;
  outcome: ReasoningOutcome;
  outcome_confidence: number | null;
  success_confidence: number | null;
  success_signal_source: string | null;
  success_evaluated_at: string | null;
  intent: string | null;
  duration_ms: number | null;
  trace_id: string | null;
  tools_count: number;
  evidence_count: number;
  created_at: string;
}

export interface ReasoningTraceListResponse {
  items: ReasoningTraceListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface ReasoningTraceToolCall {
  name: string;
  args_summary?: string;
  duration_ms?: number;
  ok: boolean;
  error?: string | null;
  /** Jun 28 2026 — tools emitted in the same Gemini ModelResponse share
   * the same id; ≥2 tools with the same group = real parallel emission.
   * Missing on legacy traces (pre-rollout). */
  parallel_group?: number;
}

export interface ReasoningTraceSummary {
  id: string;
  turn_id: string;
  conversation_id: string | null;
  plan_step: string | null;
  tools_called: ReasoningTraceToolCall[];
  evidence_content_ids: string[];
  assumptions: Record<string, unknown>;
  intent: string | null;
  outcome: ReasoningOutcome;
  outcome_confidence: number | null;
  success_confidence: number | null;
  success_signal_source: string | null;
  success_evaluated_at: string | null;
  duration_ms: number | null;
  trace_id: string | null;
  created_at: string;
  updated_at: string;
  tokens: { input: number; output: number; llm_calls: number };
  cost_usd: number;
  user_feedback: { type: string; value: number | null } | null;
}

/** GET ``/admin/hypermem/reasoning-traces`` (list + filter). */
export async function listReasoningTraces(
  tenantId: string,
  opts?: {
    user_id?: string;
    outcome?: ReasoningOutcome;
    intent?: string;
    min_success_confidence?: number;
    max_success_confidence?: number;
    limit?: number;
    offset?: number;
  },
): Promise<ReasoningTraceListResponse> {
  return apiClient.get<ReasoningTraceListResponse>(
    `/admin/hypermem/reasoning-traces${qs({
      tenant_id: tenantId,
      user_id: opts?.user_id,
      outcome: opts?.outcome,
      intent: opts?.intent,
      min_success_confidence: opts?.min_success_confidence,
      max_success_confidence: opts?.max_success_confidence,
      limit: opts?.limit ?? 100,
      offset: opts?.offset ?? 0,
    })}`,
  );
}

/** GET ``/admin/hypermem/reasoning-traces/{turn_id}/summary`` (drill-down). */
export async function getReasoningTraceSummary(
  turnId: string,
  userId: string,
): Promise<ReasoningTraceSummary> {
  return apiClient.get<ReasoningTraceSummary>(
    `/admin/hypermem/reasoning-traces/${encodeURIComponent(turnId)}/summary${qs({
      user_id: userId,
    })}`,
  );
}
